import { WhiteLabelType } from '../../types/WhiteLabelType';
import {
  Auth0Parameter,
  Auth0Prompt,
  Auth0ScreenHint,
} from '../../types/auth0/Auth0ParameterType';
import { imageUrl, colorToHex } from '../formatters';
import getTextColor from '../getTextColor';
import { ENVIRONMENTS } from 'constants-activate';

import { DefaultLocale } from 'localization';

const localeMapping = {
  // Locales that use for the Auth0 UI_locales parameter
  shortenedLocales: [
    'de-AT', // German (Austrian) -> de
    'de-DE', // German -> de
    'en-GB', // English (UK) -> en
    'en-US', // English (US) -> en
    'es-ES', // Spanish (Spain) -> es
    'it-IT', // Italian -> it
    'ja-JP', // Japanese -> ja
    'nl-NL', // Dutch -> nl
    'th-TH', // Thai -> th
  ],

  fullLocales: [
    'ca-ES', // Catalan -> ca-ES
    'es-MX', // Spanish (Mexico) -> es-MX
    'fr-CA', // French (Canadian) -> fr-CA
    'fr-FR', // French (France) -> fr-FR
    'pt-BR', // Portuguese (Brazil) -> pt-BR
    'pt-PT', // Portuguese (Portugal) -> pt-PT
    'zh-HK', // Chinese (Traditional) -> zh-HK
  ],
};

/**
 * Builds Auth0 language mapping dynamically from server-config.
 * Two categories:
 * 1. Shortened locales: keep only the language code (part before hyphen)
 * 2. Full locales: keep the complete locale string
 */
function buildAuth0LanguageMapping(): Record<string, string> {
  const { shortenedLocales, fullLocales } = localeMapping;
  const mapping: Record<string, string> = {};

  shortenedLocales.forEach(locale => {
    const languageCode = locale.split('-')[0];

    mapping[locale] = languageCode;
  });

  fullLocales.forEach(locale => {
    mapping[locale] = locale;
  });

  return mapping;
}

const languageMapping = buildAuth0LanguageMapping();


export default function getAuth0Parameter(
  whitelabel: WhiteLabelType,
  email: string,
  isUserExists: boolean,
  invitation?: string,
  searchParamInviteId?: string,
  connection?: string,
  hideAuth0LoginAlertBox?: boolean,
  userLocale?: string
) {
  
  const logo = imageUrl(whitelabel.profile.logo);
  const backgroundImage = imageUrl(whitelabel.profile?.backgroundImage);
  const pageTitle = whitelabel.profile.name;
  const primaryColor = colorToHex(whitelabel.profile.backgroundColor);
  const primaryButtonColor = getTextColor(whitelabel.profile.backgroundColor);

  const isAuth0SimulatorEnabled =
    (process.env.APP_ENV === ENVIRONMENTS.LOCAL ||
      process.env.APP_ENV === ENVIRONMENTS.TEST) &&
    process.env.AUTH0_SIMULATOR_ENABLED === 'true';

  const authorizationParams: Auth0Parameter = {
    login_hint: email,
    screen_hint: Auth0ScreenHint.signup,
    ...(!isAuth0SimulatorEnabled && {
      organization: whitelabel.auth0OrganizationId || whitelabel.instance,
    }),
  };

  if (searchParamInviteId) {
    authorizationParams.aiid = searchParamInviteId;
  }

  if (pageTitle) {
    authorizationParams['ext-page-title'] = pageTitle;
  }

  if (primaryColor) {
    authorizationParams['ext-primary-color'] = primaryColor.replace('#', '');
  }

  if (primaryButtonColor) {
    authorizationParams['ext-primary-button-color'] =
      primaryButtonColor.replace('#', '');
  }

  if (logo) {
    authorizationParams['ext-background-logo'] = logo;
  }

  if (backgroundImage) {
    authorizationParams['ext-background-image'] = backgroundImage;
  }

  if (isUserExists) {
    authorizationParams.screen_hint = Auth0ScreenHint.login;
  } else if (invitation) {
    authorizationParams.invitation = invitation;
  }

  if (!connection && hideAuth0LoginAlertBox) {
    authorizationParams.prompt = Auth0Prompt.login;
  }

  if (connection && connection === 'email' && hideAuth0LoginAlertBox) {
    authorizationParams.connection = connection;
    authorizationParams.prompt = Auth0Prompt.login;
  }
  
  authorizationParams.ui_locales = 
  (userLocale && userLocale in languageMapping 
    ? languageMapping[userLocale] 
    : languageMapping[DefaultLocale]
  ) ?? languageMapping[DefaultLocale];

  return authorizationParams;
}
